/**
 * What: Confirms permanent deletion of a Shopify-linked study setup.
 * Transport / data-flow: Presentational Polaris modal that delegates the confirmed request to its caller.
 *
 * Uses the classic Polaris Modal (not s-modal): React-attached handlers inside s-modal
 * do not fire in production (see 0d696db584), so the confirm/cancel actions live here.
 */
import { BlockStack, Modal, Text } from '@shopify/polaris'
import type { ComponentProps, ReactNode } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { BulkStudyDeletionItem } from '../hooks/study-builder/useBulkStudyDeletion'

const copy = shopifyComponentCopy.studySetupDeleteDialog

/** Shares the app's existing React Polaris modal boundary with other modal flows. */
export function ShopifyPolarisModal({
  open,
  onClose,
  title,
  children,
  size = 'large',
  primaryAction,
  secondaryActions,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  size?: ComponentProps<typeof Modal>['size']
  primaryAction?: ComponentProps<typeof Modal>['primaryAction']
  secondaryActions?: ComponentProps<typeof Modal>['secondaryActions']
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size={size}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <Modal.Section>{children}</Modal.Section>
    </Modal>
  )
}

/** Keeps the destructive confirmation mounted so Polaris can manage focus and exit transitions. */
export function StudySetupDeleteDialog({
  open,
  productTitle,
  isDeleting,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  open: boolean
  productTitle: string
  isDeleting: boolean
  errorMessage?: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={copy.title001}
      primaryAction={{
        content: copy.confirm005,
        destructive: true,
        loading: isDeleting,
        disabled: isDeleting,
        onAction: onConfirm,
      }}
      secondaryActions={[{ content: copy.cancel004, onAction: onCancel, disabled: isDeleting }]}
    >
      <Modal.Section>
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {copy.productLabel002}: {productTitle}
          </Text>
          <Text as="p">{copy.description003}</Text>
          <Text as="p">Credit receipts are kept. Eligible failed tests receive their credits back automatically.</Text>
          {errorMessage && (
            <Text as="p" tone="critical">
              {errorMessage}
            </Text>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  )
}

type BulkDeletionAction = 'delete' | 'check' | 'retry' | 'mixed'

function bulkDialogTitle(
  phase: 'confirming' | 'running' | 'outcomes',
  items: BulkStudyDeletionItem[],
  actionCount: number,
  action: BulkDeletionAction,
) {
  const copy = shopifyComponentCopy.studySetupBulkDeleteDialog
  const count = phase === 'outcomes' ? items.length : actionCount
  if (count === 0) return copy.noSelectionTitle
  if (phase === 'confirming') {
    if (action === 'check') return count === 1 ? copy.statusSingular : copy.statusPlural(count)
    if (action === 'retry') return count === 1 ? copy.retryTitleSingular : copy.retryTitlePlural(count)
    return count === 1 ? copy.titleSingular : copy.titlePlural(count)
  }
  if (phase === 'running') {
    if (action === 'check') return count === 1 ? copy.checkingSingular : copy.checkingPlural(count)
    return count === 1 ? copy.runningSingular : copy.runningPlural(count)
  }
  return count === 1 ? copy.statusSingular : copy.statusPlural(count)
}

function bulkConfirmLabel(
  phase: 'confirming' | 'running' | 'outcomes',
  actionCount: number,
  action: BulkDeletionAction,
) {
  const copy = shopifyComponentCopy.studySetupBulkDeleteDialog
  if (phase === 'running') return actionCount === 1 ? copy.runningSingular : copy.runningPlural(actionCount)
  if (action === 'check') return copy.confirmCheck
  if (action === 'retry') return copy.confirmRetry
  if (action === 'mixed') return copy.confirmContinue
  return actionCount === 1 ? copy.confirmSingular : copy.confirmPlural(actionCount)
}

/** Confirms selected test deletion and presents durable per-test cleanup progress. */
export function BulkStudyDeleteDialog({
  open,
  phase,
  confirmationAction,
  actionCount,
  canConfirm,
  items,
  isDeleting,
  message,
  onClose,
  onConfirm,
  onCheckPending,
  onRetryFailed,
}: {
  open: boolean
  phase: 'confirming' | 'running' | 'outcomes'
  confirmationAction: BulkDeletionAction
  actionCount: number
  canConfirm: boolean
  items: BulkStudyDeletionItem[]
  isDeleting: boolean
  message?: string | null
  onClose: () => void
  onConfirm: () => void
  onCheckPending: () => void
  onRetryFailed: () => void
}) {
  const copy = shopifyComponentCopy.studySetupBulkDeleteDialog
  const completedCount = items.filter((item) => item.status === 'complete').length
  const pendingCount = items.filter((item) => item.status === 'pending').length
  const failedItems = items.filter((item) => item.status === 'failed')
  const outcomeSummary = copy.outcomeSummary(completedCount, pendingCount, failedItems.length)
  const actionInProgress = isDeleting || phase === 'running'
  const primaryAction =
    phase === 'outcomes' || !canConfirm
      ? undefined
      : {
          content: bulkConfirmLabel(phase, actionCount, confirmationAction),
          destructive: phase === 'confirming' && confirmationAction !== 'check',
          loading: actionInProgress,
          disabled: actionInProgress || !canConfirm,
          onAction: onConfirm,
        }
  const secondaryActions = []
  if (!actionInProgress && phase === 'outcomes' && pendingCount > 0)
    secondaryActions.push({ content: copy.checkPending, onAction: onCheckPending })
  if (!actionInProgress && phase === 'outcomes' && failedItems.length > 0)
    secondaryActions.push({ content: copy.retryFailed, destructive: true, onAction: onRetryFailed })
  secondaryActions.push({
    content: actionInProgress || phase === 'outcomes' ? copy.closeProgress : copy.cancel,
    onAction: onClose,
  })
  const title = bulkDialogTitle(phase, items, actionCount, confirmationAction)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <Modal.Section>
        <BlockStack gap="200">
          {message && (
            <Text as="p" tone="critical">
              {message}
            </Text>
          )}
          {phase === 'confirming' && <Text as="p">{copy.permanentRemoval}</Text>}
          {phase === 'running' && <Text as="p">{outcomeSummary}</Text>}
          {phase === 'running' && <Text as="p">{copy.continuedInBackground}</Text>}
          {phase === 'outcomes' && <Text as="p">{outcomeSummary}</Text>}
          {phase === 'outcomes' && pendingCount > 0 && (
            <Text as="p" tone="subdued">
              {copy.continuedInBackground}
            </Text>
          )}
          <div style={{ maxHeight: '45vh', overflowY: 'auto' }} aria-label={copy.selectedTestsList}>
            <BlockStack gap="200">
              {items.map((item) => (
                <BlockStack key={item.id} gap="050">
                  <Text as="p" fontWeight="semibold">
                    {item.title}
                  </Text>
                  <Text as="p" tone="subdued">
                    {item.productTitle || copy.productUnavailable}
                  </Text>
                  {item.status === 'complete' && (
                    <Text as="p" tone="subdued">
                      {copy.deleted}
                    </Text>
                  )}
                  {item.status === 'pending' && (
                    <Text as="p" tone="subdued">
                      {copy.deleting}
                    </Text>
                  )}
                  {item.status === 'pending' && item.error && (
                    <Text as="p" tone="critical">
                      {item.error}
                    </Text>
                  )}
                  {item.status === 'failed' && (
                    <Text as="p" tone="critical">
                      {item.error || copy.failed}
                    </Text>
                  )}
                </BlockStack>
              ))}
            </BlockStack>
          </div>
        </BlockStack>
      </Modal.Section>
    </Modal>
  )
}
